<script>
import { mapState, mapActions } from 'vuex';
import TwConfirmDialog from '../TwConfirmDialog';
import TwOverlay from '../TwOverlay';
import escapable from '../../mixins/escapable';

export default {
  name: 'AffiliatesSuppliers',
  components: {
    TwConfirmDialog,
    TwOverlay,
  },
  mixins: [
    escapable,
  ],
  data() {
    return {
      headers: [
        { value: 'supplierName', text: 'Supplier' },
        { value: 'productTypeName', text: 'Product' },
        { value: 'domains', text: 'Domains' },
        { value: '', text: 'Action', sortable: false, width: '110px' },
      ],
      chosenSupplier: undefined,
      chosenAffiliateSupplier: undefined,
      checkboxes: [],
      newSupplierShow: false,
      snackbarText: undefined,
      snackbarShow: false,
      supplierIdToRemove: undefined,
      pagination: {
        rowsPerPage: 10,
      },
    };
  },
  computed: {
    ...mapState({
      affiliate: state => state.affiliates.affiliate.item,
      affiliateSuppliers: state => state.affiliates.affiliateSuppliers,
      suppliers: state => state.affiliates.suppliers,
    }),
  },
  methods: {
    ...mapActions({
      getSuppliers: 'affiliates/getSuppliers',
      addSupplier: 'affiliates/addSupplier',
      syncSupplier: 'affiliates/syncSupplier',
      removeSupplier: 'affiliates/deleteSupplier',
    }),
    openNewSupplier() {
      this.newSupplierShow = true;
      // Waiting for transition
      setTimeout(() => {
        this.$refs.newSupplier.focus();
      }, 350);

      if (!this.suppliers.items.length) {
        this.getSuppliers();
      }
    },
    addNewSupplier() {
      if (this.affiliateSuppliers.items.some(s => s.supplierId === this.chosenSupplier.id)) {
        this.snackbarText = 'This supplier is already added to the affiliate';
        this.snackbarShow = true;
      } else {
        const newSupplier = {
          supplierName: this.chosenSupplier.name,
          productTypeName: this.chosenSupplier.productType,
          supplierId: this.chosenSupplier.id,
          domains: [],
        };
        this.addSupplier(newSupplier);
        this.chosenAffiliateSupplier = newSupplier;
        this.newSupplierShow = false;
        this.chosenSupplier = undefined;
      }
    },
    deleteSupplier() {
      const supplier = this.affiliateSuppliers.items.find(s => s.supplierId === this.supplierIdToRemove);
      if (supplier.domains.length === 0) {
        this.removeSupplier(this.supplierIdToRemove);
      } else {
        this.syncSupplier({
          supplierId: this.supplierIdToRemove,
          affiliateDomainIds: [],
        });
      }
      if (this.chosenAffiliateSupplier.supplierId === supplier.supplierId) {
        this.chosenAffiliateSupplier = undefined;
      }
      this.cancelDeleteSupplier();
    },
    confirmDeleteSupplier(supplierId) {
      this.supplierIdToRemove = supplierId;
      this.$refs.confirm.open();
    },
    cancelDeleteSupplier() {
      this.supplierIdToRemove = undefined;
    },
    saveSupplier() {
      if (!this.checkboxes.length) {
        this.supplierIdToRemove = this.chosenAffiliateSupplier.supplierId;
        this.$refs.confirm.open();
      } else {
        this.syncSupplier({
          supplierId: this.chosenAffiliateSupplier.supplierId,
          affiliateDomainIds: this.checkboxes,
        });
      }
    },
    escapeHandler() {
      this.newSupplierShow = false;
      this.chosenSupplier = undefined;
    },
  },
  watch: {
    chosenAffiliateSupplier() {
      if (this.chosenAffiliateSupplier) {
        const checkboxes = [];
        this.chosenAffiliateSupplier.domains.forEach((domain) => {
          checkboxes.push(domain);
        });
        this.checkboxes = checkboxes;
      } else {
        this.checkboxes = [];
      }
    },
    affiliate() {
      this.chosenAffiliateSupplier = undefined;
    },
  },
};
</script>

<template>
  <v-flex v-if="affiliate" xs12>
    <v-layout wrap mt-4 affiliates-suppliers>
      <tw-overlay :visible="affiliateSuppliers.loading"/>
      <v-progress-circular v-if="affiliateSuppliers.loading" class="affiliates-suppliers__loading" indeterminate/>
      <v-flex xs12 mt-4 mb-2>
        <span class="title">Suppliers</span>
      </v-flex>
      <v-flex xs12>
        <v-divider/>
      </v-flex>
      <v-flex xs12 pt-3>
        <template v-if="chosenAffiliateSupplier">
          <span class="title grey--text text--lighten-1">
            Currently showing domains for
          </span>
          <span class="title text--lighten-1 font-weight-bold">
            {{ chosenAffiliateSupplier.supplierName }}
            ({{ chosenAffiliateSupplier.productTypeName }})
          </span>
          <v-checkbox
            v-for="domain in affiliate.domains" :key="domain.id"
            v-model="checkboxes"
            :label="domain.domain.name"
            :value="domain.id"
            hide-details
          />
          <v-btn class="my-4" color="info" @click="saveSupplier">Save</v-btn>
        </template>
        <div v-else class="affiliates-suppliers__domains-placeholder d-flex">
          <v-alert
            :value="true"
            class="align-center"
            type="info"
            outline
          >
            Select or add a supplier to attach domains here
          </v-alert>
        </div>
      </v-flex>
      <v-flex xs12>
        <v-data-table
          :items="affiliateSuppliers.items"
          :headers="headers"
          :pagination.sync="pagination"
        >
          <template v-slot:items="props">
            <tr :class="{
              'info lighten-5': chosenAffiliateSupplier
              && props.item.supplierId === chosenAffiliateSupplier.supplierId
            }">
              <td>{{ props.item.supplierName }}</td>
              <td>{{ props.item.productTypeName }}</td>
              <td>{{ props.item.domains.length }}</td>
              <td>
                <v-btn class="ma-0" small icon @click="chosenAffiliateSupplier = props.item">
                  <v-icon small>edit</v-icon>
                </v-btn>
                <v-btn class="ma-0" small icon @click="confirmDeleteSupplier(props.item.supplierId)">
                  <v-icon small>delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
          <template v-slot:footer>
            <td :colspan="headers.length" class="text-xs-center">
              <v-btn icon @click="openNewSupplier">
                <v-icon class="grey--text">add</v-icon>
              </v-btn>
            </td>
          </template>
        </v-data-table>
      </v-flex>
      <v-dialog
        v-model="newSupplierShow"
        max-width="600px"
        persistent
        no-click-animation
      >
        <v-card>
          <v-card-title>
            <h1>Add new supplier</h1>
            <v-spacer/>
            <v-btn icon small @click="newSupplierShow = false">
              <v-icon small>close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider/>
          <v-card-text>
            <v-alert :value="true" type="info" outline>
              Note: A supplier will only be saved once a domain is added
            </v-alert>
            <v-autocomplete
              v-model="chosenSupplier"
              :items="suppliers.items"
              :loading="suppliers.loading"
              :item-text="i => `${i.name} (${i.productType})`"
              class="mt-3"
              placeholder="Select supplier"
              ref="newSupplier"
              return-object
              clearable
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer/>
            <v-btn :disabled="!chosenSupplier" color="info" @click="addNewSupplier">Add</v-btn>
          </v-card-actions>
        </v-card>
        <v-snackbar v-model="snackbarShow">{{ snackbarText }}</v-snackbar>
      </v-dialog>
      <tw-confirm-dialog
        ref="confirm"
        title="Delete Supplier?"
        description="Removing all domains from this supplier, will delete the supplier."
        @confirm="deleteSupplier"
        @cancel="cancelDeleteSupplier"
      />
    </v-layout>
  </v-flex>
</template>

<style lang="scss">
.affiliates-suppliers {
  position: relative;

  &__loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 100;
  }

  &__domains-placeholder {
    height: 200px;
    padding: 50px 0 50px 0;
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Affiliates/AffiliatesSuppliers.vue